import SiFolder from '@/icons/SiFolder';
import { cn } from '@/lib/utils';
import { useExplorer, useItem, useIsOpen } from '@/services/explorer/useExplorerProvider';
import { ItemTree } from '@/utils/explorer';

import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface ItemProps {
  directoryId: string;
  tree: ItemTree;
}

const useIsActive = (currentDirectoryId: string, currentItemId: string): boolean => {
  const { directoryId, itemId } = useParams<{ directoryId: string; itemId: string }>();
  if (!directoryId || !itemId) return false;

  return currentDirectoryId === directoryId && currentItemId === itemId;
};

const Folder: FC<ItemProps> = ({ directoryId, tree }) => {
  const prefix = useExplorer(state => state.prefix);
  const item = useItem(directoryId, tree.id);
  const [isOpen, setOpenState] = useIsOpen(directoryId, tree.id);
  const nested = tree.items?.filter(child => child.type === 'folder');

  const navigate = useNavigate();
  const toggleOpenState = () => setOpenState(directoryId, tree.id, !isOpen);
  const navigateToFolder = () => navigate(`${prefix}/${directoryId}/${tree.id}`);

  const isActive = useIsActive(directoryId, tree.id);

  return (
    <div>
      <div className="flex items-center">
        {nested && nested.length > 0 ? (
          <button
            className="flex h-5 w-5 items-center justify-center rounded-md p-0.5 hover:bg-gray-50"
            onClick={toggleOpenState}
          >
            <motion.svg
              initial={{
                rotate: isOpen ? 90 : 0
              }}
              animate={{
                rotate: isOpen ? 90 : 0
              }}
              width="5"
              height="8"
              viewBox="0 0 5 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.242919 0.300756C0.584557 -0.0801482 1.15977 -0.102204 1.52769 0.251492L4.7095 3.31031C4.89474 3.48839 5 3.73829 5 4C5 4.26171 4.89474 4.51161 4.7095 4.68969L1.52769 7.74851C1.15977 8.1022 0.584557 8.08015 0.242919 7.69924C-0.0987198 7.31834 -0.0774159 6.72283 0.290503 6.36913L2.7549 4L0.290503 1.63087C-0.0774159 1.27717 -0.0987198 0.681659 0.242919 0.300756Z"
                fill="black"
              />
            </motion.svg>
          </button>
        ) : (
          <span className="h-5 w-5" />
        )}
        <button
          className={cn(
            'flex w-full items-center gap-1 rounded-md py-0.5 hover:bg-gray-50',
            isActive && 'font-semibold text-[#001489]'
          )}
          onClick={navigateToFolder}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <SiFolder />
          </span>
          <p>{item.name}</p>
        </button>
      </div>
      <div className="ml-[1.125rem]">
        {isOpen && nested && nested.map(child => <Folder key={child.id} directoryId={directoryId} tree={child} />)}
      </div>
    </div>
  );
};

const Explorer: FC<{ id: string }> = ({ id }) => {
  const directory = useExplorer(state => state.directories[id]);
  const item = useItem(id, id);

  return (
    <div>
      <label className="text-[.8rem] uppercase font-semibold text-gray-500">{item.name}</label>
      <Folder directoryId={id} tree={directory.tree} />
    </div>
  );
};

type SidebarState = 'open' | 'closed';
const styleForOpenState = (state: SidebarState) => {
  switch (state) {
    case 'open':
      return {
        width: '18rem',
        minWidth: '18rem'
      };
    case 'closed':
      return {
        width: '4rem',
        minWidth: '4rem'
      };
  }
};

const toggleState = (state: SidebarState): SidebarState => {
  switch (state) {
    case 'open':
      return 'closed';
    case 'closed':
      return 'open';
  }
};

export interface DirectoryNavigationConfig {}

export const ExplorerSidebar: FC<DirectoryNavigationConfig> = () => {
  const [state, setSidebarState] = useState<SidebarState>('open');
  const directories = useExplorer(state => Object.keys(state.directories));

  return (
    <motion.div
      animate={styleForOpenState(state)}
      className="border-secondary-std bg-white bg-opacity-80 flex border-r"
    >
      <div className="flex min-w-[18rem] flex-col gap-2 px-4 py-5 overflow-y-auto">
        <div className="flex justify-between">
          <button
            className="bg-secondary-std border-primary-std flex h-8 w-8 items-center justify-center rounded-lg border"
            onClick={() => setSidebarState(toggleState)}
          >
            <motion.svg
              animate={{
                rotate: state === 'open' ? 0 : 180
              }}
              width="14"
              height="14"
              viewBox="0 0 14 14"
            >
              <path
                d="M11.2292 2.77075V11.2291"
                stroke="#444444"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.77081 7H8.89581"
                stroke="#444444"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.81248 5.10425L2.77081 7.00008L4.81248 8.89591"
                stroke="#444444"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </button>
        </div>
        <AnimatePresence>
          {state === 'open' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-2"
            >
              {directories.map(id => (
                <Explorer key={id} id={id} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
